import React, { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { FormInput } from "go-form";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import FormMoneyInput from "go-form/components/FormMoneyInput";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { apiOrganization } from "../../../../../../../../../../services/Api/Organization/apiOrganization";
import { EmployeeApi } from "../../../../../../../../../../services/Api/Organization/types";

interface Props {
	form: UseFormReturn<EmployeeApi>;
	employee?: EmployeeApi;
	setNewAvatar: React.Dispatch<React.SetStateAction<string | undefined>>;
	newAvatar: string | undefined;
}

const EmployeeBasicInfoForm = ({ form, employee, newAvatar, setNewAvatar }: Props) => {
	const { t } = useTranslation();
	const {
		register,
		formState: { errors },
		watch,
		control,
	} = form;
	const organization = useSelector(selectOrganization);
	const currency = organization.currency || "PLN";
	const [showImage, setShowImage] = useState(false);

	const searchRoles = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getRolesSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const searchWorkplaces = (search: string, params: Record<string, any>, options?: Record<string, any>) => {
		return apiOrganization.getWorkplacesSearchSelect(search, params, {
			cancelToken: options?.token,
		});
	};

	const handleSaveAvatar = (imageBase: string | undefined) => {
		setNewAvatar(imageBase);
	};

	return (
		<fieldset className="form-group">
			<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
			<div className="row">
				<div className="col-md-10">
					<div className="row">
						<div className="col-md-6">
							<FormInput
								label={t("common.word.name", { ns: "lib" })}
								register={register}
								name="name"
								errors={errors}
							/>
						</div>
						<div className={"col-md-6"}>
							<FormSelectGroup
								label={t("modules.employee.field.role.title")}
								name="venue_role_id"
								placeholder={t("modules.employee.action.role_placeholder.title")}
								errors={errors}
								getOptionValue={(option) => option.id}
								defaultValue={{
									id: employee?.venue_role_id,
									label: employee?.venue_role?.name,
								}}
								loadOptions={searchRoles}
								control={control}
								data-testid="venue_role_id"
							/>
						</div>
						<div className={"col-md-6"}>
							<FormSelectGroup
								label={t("modules.employee.field.workplace.title")}
								name="workplace_id"
								placeholder={t("modules.employee.field.workplace_placeholder.title")}
								errors={errors}
								getOptionValue={(option) => option.id}
								defaultValue={{
									label: employee?.workplace?.name,
									id: employee?.workplace_id,
								}}
								loadOptions={searchWorkplaces}
								control={control}
								data-testid="workplace_id"
							/>
						</div>
						<div className={"col-md-6"}>
							<FormMoneyInput
								label={t("modules.employee.field.hourly_rate_net.title")}
								control={control}
								currency={currency}
								name="hourly_rate.amount"
								errors={errors}
							/>
						</div>
					</div>
				</div>
				<div className={"col-md-2"} style={{ minWidth: "150px" }}>
					<ImageColorForm
						form={form}
						handleOpen={() => setShowImage(true)}
						handleSave={handleSaveAvatar}
						resourceLink={employee?.avatar_link || watch("avatar_link")}
						resourceData={employee?.avatar || newAvatar}
						mode="IMAGE_COLOR"
						displayColor
						prefix="avatar"
						show={showImage}
						data={Object.keys(employee || {}).length > 0 ? employee : watch()}
						setTextDisabled
						placeholder="/graphics/user-avatar.svg"
					/>
				</div>
			</div>
		</fieldset>
	);
};

export default EmployeeBasicInfoForm;
